import React from "react";
import { useTranslation } from "react-i18next";
import MerchantSearchAutoComplete, {
  Merchant,
} from "components/common/MerchantSearchAutoComplete";
import { FormikProps } from "formik";
import { titleCase } from "title-case";
import MerchantSearchType from "enums/MerchantSearchType";
import { useAllocationSelectOptions } from "hooks/useAllocationSelectOptions";
import { CashExpense } from "utils/reimbursements/reimbursementExpenseFormat";
import { useFormikHelpers } from "hooks/useFormikHelpers";
import { Stack } from "components/layout/reusableLayouts";
import { Select, TextField } from "@outpave/ds";
import { Typography } from "@mui/material";

interface CashReimbursementFormProps {
  formikCreateExpense: FormikProps<CashExpense>;
}

/**
 * Employee self-service port of the admin `CashExpenseForm`. The admin
 * "Pay to" `EmployeeSearchAutoComplete` is intentionally omitted here: in the
 * self-service flow the payee is always the logged-in user, so the parent
 * drawer locks `payTo` and no employee picker is rendered. All other fields,
 * handlers, and validation match the admin form; Tailwind wrappers are
 * replaced with DS `Stack` primitives.
 */
const CashReimbursementForm: React.FC<CashReimbursementFormProps> = ({
  formikCreateExpense,
}) => {
  const { t } = useTranslation();
  const { handleFormikHelperText } = useFormikHelpers(formikCreateExpense);

  // Seeded with the saved allocation so the field renders its own value before
  // any search runs; the DS resolves a label only from `options`.
  const allocation = useAllocationSelectOptions(
    formikCreateExpense.values.allocationCode?.id
      ? {
          id: formikCreateExpense.values.allocationCode.id,
          allocationCode:
            formikCreateExpense.values.allocationCode.allocationCode,
          allocationTypeName: formikCreateExpense.values.allocationType?.name,
        }
      : null,
  );

  const clearAllocation = () => {
    formikCreateExpense.setFieldValue("allocationCode", {
      id: "",
      allocationCode: "",
    });
    formikCreateExpense.setFieldValue("allocationType", { key: "", name: "" });
  };

  const handleSelectMerchant = (merchant: Merchant | null) => {
    formikCreateExpense.setFieldValue("merchant", {
      id: merchant?.id || "",
      name: merchant?.name ? titleCase(merchant?.name.toLowerCase()) : "",
      logo: merchant?.logo || "",
      cachedLogo: merchant?.cachedLogo || "",
      url: merchant?.url || "",
    });
  };

  return (
    <Stack gap="var(--space-8)" sx={{ width: "100%" }}>
      <MerchantSearchAutoComplete
        title={t("Merchant")}
        merchant={formikCreateExpense.values?.merchant || null}
        onSelectMerchant={handleSelectMerchant}
        allowUseSearchValue={true}
        type={MerchantSearchType.brand}
        errorText={handleFormikHelperText("merchant")}
        handleBlur={() =>
          formikCreateExpense.setTouched({
            ...formikCreateExpense.touched,
            merchant: true,
          })
        }
        required={true}
      />

      <TextField
        fullWidth
        type="number"
        name="amount"
        label={t("Amount")}
        required
        value={formikCreateExpense.values.amount}
        onChange={formikCreateExpense.handleChange}
        onBlur={(e) => {
          const roundedValue = parseFloat(Number(e.target.value).toFixed(2));
          formikCreateExpense.setFieldValue("amount", roundedValue);
          formikCreateExpense.handleBlur(e);
        }}
        leading={
          <Typography
            variant="body2"
            sx={{
              color: "var(--color-text-secondary)",
            }}
          >
            $
          </Typography>
        }
        inputProps={{
          onWheel: (event) => event.currentTarget.blur(), // Disable scroll wheel increment/decrement
          min: 0,
          step: 0.01,
        }}
        errorText={handleFormikHelperText("amount") || undefined}
      />

      <Select
        label={t("Allocation")}
        options={allocation.options}
        loading={allocation.loading}
        onInputChange={allocation.onInputChange}
        onScrollEnd={allocation.onScrollEnd}
        noOptionsText={allocation.noOptionsText}
        bottomAction={allocation.bottomAction}
        value={formikCreateExpense.values.allocationCode?.id || null}
        onChange={(id) => {
          if (!id) {
            clearAllocation();
            // The DS clear fires onChange only, so the list would stay filtered.
            allocation.reset();
            return;
          }
          const picked = allocation.allocationById(id);
          formikCreateExpense.setFieldValue("allocationCode", {
            id,
            allocationCode: picked?.allocationCode ?? "",
          });
          formikCreateExpense.setFieldValue("allocationType", {
            key: "",
            name: picked?.allocationTypeName ?? "",
          });
        }}
        placeholder={t("Search allocations")}
        errorText={handleFormikHelperText("allocationCode") || undefined}
        fullWidth
      />

      <TextField
        fullWidth
        name="description"
        label={t("Description")}
        value={formikCreateExpense.values.description}
        onChange={formikCreateExpense.handleChange}
        onBlur={formikCreateExpense.handleBlur}
        placeholder={t("Business Purpose for reimbursement")}
        required
        errorText={handleFormikHelperText("description") || undefined}
      />
    </Stack>
  );
};

export default CashReimbursementForm;
